import { api } from '../api';
import { TokenService } from '../tokenService';
import { sendToTerminal } from '../terminalLogger';
import { validateMeResponse } from './validators';

export interface AuthStateReconcileOptions {
	reason: string;
	timeoutMs?: number;
}

export interface AuthStateReconcileResult {
	success: boolean;
	error?: string;
}

type ReconcileCycleMeta = {
	token: string;
	sessionEpoch: number;
	tokenVersion: number;
	reason: string;
};

let lastReconciled: ReconcileCycleMeta | null = null;
const inflightReconciles = new Map<string, Promise<AuthStateReconcileResult>>();

/**
 * Reconciliador canônico do estado de autenticação.
 * Sempre que um access token novo entrar em cena por bootstrap/reinjeção/refresh,
 * este helper é o ponto único para revalidar `/auth/me` e sincronizar `userData`.
 */
export async function reconcileAuthState(
	accessToken: string,
	options: AuthStateReconcileOptions & {
		sessionEpoch?: number;
		tokenVersion?: number;
	},
): Promise<AuthStateReconcileResult> {
	const timeoutMs = options.timeoutMs ?? 5000;
	const sessionEpoch = options.sessionEpoch ?? Number((window as any)?.sessionEpoch || 0);
	const tokenVersion = options.tokenVersion ?? Number((window as any)?.tokenVersion || 0);

	if (!accessToken) {
		return { success: false, error: 'accessToken ausente para reconciliar auth' };
	}

	if (
		lastReconciled &&
		lastReconciled.token === accessToken &&
		lastReconciled.sessionEpoch === sessionEpoch &&
		lastReconciled.tokenVersion === tokenVersion
	) {
		sendToTerminal('⏭️ [AUTH_RECONCILE] reconcile ignorado (ciclo já aplicado)', {
			reason: options.reason,
			sessionEpoch,
			tokenVersion,
		});
		return { success: true };
	}

	const reconcileKey = `${accessToken}::${sessionEpoch}::${tokenVersion}`;
	const inflight = inflightReconciles.get(reconcileKey);
	if (inflight) {
		sendToTerminal('⏳ [AUTH_RECONCILE] reconcile já em andamento para este ciclo', {
			reason: options.reason,
			sessionEpoch,
			tokenVersion,
		});
		return inflight;
	}

	const reconcilePromise = (async (): Promise<AuthStateReconcileResult> => {
		try {
			const meResponse = await Promise.race([
				api('/auth/me', { method: 'GET' }),
				new Promise<never>((_, reject) =>
					setTimeout(
						() =>
							reject(
								new Error(`Timeout ${timeoutMs}ms ao buscar /auth/me`),
							),
						timeoutMs,
					),
				),
			]);

			const validation = validateMeResponse(meResponse, accessToken);
			if (!validation.success) {
				sendToTerminal('⚠️ [AUTH_RECONCILE] /auth/me inválido', {
					reason: options.reason,
					error: validation.error,
				});
				return { success: false, error: validation.error };
			}

			TokenService.setUserData(validation.normalized);
			lastReconciled = {
				token: accessToken,
				sessionEpoch,
				tokenVersion,
				reason: options.reason,
			};

			sendToTerminal('✅ [AUTH_RECONCILE] userData reconciliado via /auth/me', {
				reason: options.reason,
				userId: validation.normalized.data.usuario.id,
				sessionEpoch,
				tokenVersion,
				permissionsConfigCount:
					validation.normalized.data.permissionsConfig?.length || 0,
			});

			return { success: true };
		} catch (error) {
			const message =
				error instanceof Error ? error.message : String(error);
			sendToTerminal('⚠️ [AUTH_RECONCILE] falha ao reconciliar auth via /auth/me', {
				reason: options.reason,
				error: message,
			});
			return { success: false, error: message };
		}
	})();

	inflightReconciles.set(reconcileKey, reconcilePromise);
	try {
		return await reconcilePromise;
	} finally {
		inflightReconciles.delete(reconcileKey);
	}
}
